// src/utils/teacherLoad.ts
//
// One definition of a teacher's AVAILABLE minutes.
//
// Issue #93 is that the app has two parallel getTeacherSemesterLoad engines
// (AllocationContext and StabilizedContexts) plus page-local variants, each with
// its own copy of this arithmetic. They had drifted:
//
//   - AllocationContext summed semesterData.allowances AND staffMember.both
//     .allowances, filtered only by appliesTo, with no year filter.
//   - StabilizedContexts summed semesterData.allowances only — omitting `both`
//     entirely — also with no year filter.
//   - Neither used the staff member's own baseMinutesPerFortnight consistently.
//
// The missing year filter became a live bug in v2.11.0: allowances gained a year
// and every record was backfilled into BOTH 2026 and 2027, so an inline sum with
// no year filter counted each allowance twice. That halved people's apparent
// available time on the Staff Directory load column and in the Grid Editor's
// load panels, showing staff as overloaded who were not.
//
// This is the allocated/available split's *available* half only. Unifying the
// *allocated* half is the larger remainder of #93: the two engines legitimately
// differ in scope (active grid vs all grids) and wrongly differ in method (flat
// DEFAULT_MINUTES_PER_LINE vs actual minutesPerWeek), and reconciling that
// changes numbers on screens in active use.

import { getTotalAllowanceMinutes } from '../types/staff';
import { DEFAULT_BASE_MINUTES_PER_FORTNIGHT } from '../types/schoolConfig';
import { resolveClassMinutes } from './classMinutes';

export interface AvailableMinutesResult {
  /** base x FTE, before allowances. */
  baseMinutes: number;
  /** Pro-rata allowance minutes for this semester and year. */
  allowanceMinutes: number;
  /** What's left to teach with. Never negative. */
  availableMinutes: number;
}

/**
 * Minutes a teacher has available in a semester, after allowances.
 *
 * `year` is required-by-convention rather than by the type: omitting it means
 * "every year at once", which is the bug this exists to prevent. Callers that
 * genuinely do not care (a school-wide capacity total, say) can pass undefined
 * deliberately.
 */
export function computeAvailableMinutes(
  staffMember: any,
  semester: 'semester1' | 'semester2',
  year?: number
): AvailableMinutesResult {
  const fte = typeof staffMember?.fte === 'number' ? staffMember.fte : 1.0;

  // The staff member's OWN base, not the school-wide constant. 15 staff carry a
  // non-default value; measuring them against 2220 is simply wrong.
  const perFortnight =
    staffMember?.baseMinutesPerFortnight ||
    staffMember?.[semester]?.baseMinutesPerFortnight ||
    DEFAULT_BASE_MINUTES_PER_FORTNIGHT;

  const baseMinutes = Math.round(perFortnight * fte);

  // Shared, year-aware, and the single place that knows about `both` allowances.
  const allowanceMinutes = getTotalAllowanceMinutes(staffMember, semester, year);

  return {
    baseMinutes,
    allowanceMinutes,
    availableMinutes: Math.max(0, baseMinutes - allowanceMinutes),
  };
}

/** Utilisation as a whole percentage. 0 available with 0 allocated is 0%, not 100%. */
export function computeUtilisation(allocatedMinutes: number, availableMinutes: number): number {
  if (availableMinutes > 0) return Math.round((allocatedMinutes / availableMinutes) * 100);
  return allocatedMinutes > 0 ? 100 : 0;
}

/** Minimal shape of a class for load purposes. */
interface LoadClass {
  isCompositeSubClass?: boolean;
  lineAssignments?: Record<string, unknown> | null;
  classMinutes?: number;
  minutesPerWeek?: number;
  sourceGridDefaultMinutes?: number;
}

interface LoadGrid {
  classes?: LoadClass[] | null;
  defaultClassMinutes?: number;
}

/** Every code a staff member might be recorded under in lineAssignments. */
function codesFor(staffMember: any): string[] {
  return [staffMember?.staffCode, staffMember?.initial, staffMember?.id]
    .filter((c): c is string => typeof c === 'string' && c.length > 0);
}

/**
 * Minutes a staff member is allocated within ONE grid, for the given lines.
 *
 * The caller chooses which grid(s) and which lines — that scope difference
 * between the engines is legitimate (Staff Directory looks at the active grid;
 * the editor aggregates every year-scoped grid). What was NOT legitimate was
 * each engine computing the minutes differently (issue #93):
 *
 *   - AllocationContext added a flat DEFAULT_MINUTES_PER_LINE per assigned line
 *     and matched a single teacher code, so it understated every class with real
 *     minutes and missed multi-teacher assignments entirely.
 *   - StabilizedContexts used actual minutes and parsed multi-teacher JSON, but
 *     never filtered composite sub-classes (double-counting the parent's
 *     minutes) and fell back to 440 rather than the grid's own default — which
 *     counted each of the 120 assigned 2026 RUAH classes at 440 against a real
 *     default of 210.
 *
 * This combines what each got right, matching the semantics LoadSummaries
 * already used.
 */
export function computeAllocatedMinutes(
  grid: LoadGrid | null | undefined,
  staffMember: any,
  semesterLineIds: string[]
): number {
  if (!grid?.classes?.length) return 0;

  const codes = codesFor(staffMember);
  if (codes.length === 0) return 0;

  const lineSet = new Set(semesterLineIds);
  let total = 0;

  // The parent composite already carries the combined minutes; counting its
  // sub-classes as well bills the teacher twice for one lesson.
  for (const classItem of grid.classes) {
    if (classItem?.isCompositeSubClass) continue;

    for (const [lineId, rawValue] of Object.entries(classItem?.lineAssignments || {})) {
      if (!lineSet.has(lineId) || rawValue == null || rawValue === '') continue;

      // Multi-teacher cells are a JSON string: [{teacherCode, minutes}, ...].
      // Each teacher is billed only their own share, not the whole class.
      if (typeof rawValue === 'string' && (rawValue.startsWith('[') || rawValue.startsWith('{'))) {
        try {
          const parsed = JSON.parse(rawValue);
          if (Array.isArray(parsed)) {
            const mine = parsed.find((a: any) => codes.includes(a?.teacherCode));
            if (mine) total += Number(mine.minutes) || resolveClassMinutes(classItem, grid);
            continue;
          }
        } catch {
          // Not valid JSON after all — fall through and treat it as a plain code.
        }
      }

      if (typeof rawValue === 'string' && codes.includes(rawValue)) {
        total += resolveClassMinutes(classItem, grid);
      }
    }
  }

  return total;
}
